/**
 * The segmented control: timeframe, mode, unit, lane, version — one strip, one
 * filled active state, and a keyboard model that is real rather than implied.
 *
 * Two things make this a kit primitive instead of a styled row of buttons.
 *
 * The first is the tab stop. A strip of N options built from N focusable
 * buttons costs a keyboard reader N presses to walk past, and a page with four
 * such strips costs them the page. Here the strip is ONE tab stop — roving
 * tabindex, arrows to move and wrap, Home/End to jump — which is the model
 * assistive technology already expects from `role="tablist"`.
 *
 * The second is what a disabled option says. `Automatic` activation applies the
 * selection as the arrow lands on it, which is right for a switch that costs
 * nothing and wrong for one that refetches a lane; `Manual` moves focus and
 * waits for Enter or Space. And a segment that cannot be chosen explains ITSELF
 * through `disabledReason`, as a title and as an accessible description. It is
 * deliberately not `disabled`: a control with `pointer-events: none` cannot
 * show the tooltip that is the whole point of the reason existing.
 *
 * The filled pill is one element that translates between segments rather than a
 * background that blinks from cell to cell, so the eye can follow where the
 * selection went. Its position is measured, not computed from index arithmetic,
 * because segment labels are different widths and always will be.
 */
"use client";
import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import {
  crcTransition,
  CrcAnimatedProperty,
  CrcDuration,
  CrcEase,
} from "./cresultcard.motion";
import {
  CRC_ACCENT,
  CRC_R_PILL,
  CRC_TEXT_FAINT,
  CRC_TEXT_MUTED,
  CRC_TILE_BACKGROUND_COLOR,
  CRC_TILE_SHADOW,
  CRC_TOUCH_TARGET,
  CRC_TYPE_STYLE,
  CRC_WELL_BACKGROUND_COLOR,
  CRC_WELL_SHADOW,
} from "./cresultcard.tokens";
import {
  CresultcardSegmentActivation,
  CresultcardTextStep,
  type CresultcardSegmentOption,
} from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";
import "./Cresultcard.css";

const SEGMENT_PILL_CLASS_NAME = "cresultcard__segment-pill";
const QUIET_HOVER_CLASS_NAME = "cresultcard__quiet-hover";
const SEGMENT_TAB_ROLE = "tab";
const DIRECTION_RTL = "rtl";
const CURSOR_POINTER = "pointer";
const CURSOR_DEFAULT = "default";
const NOWRAP = "nowrap";

const STRIP_PAD = 3;
const SEGMENT_GAP = 2;
const SEGMENT_INLINE_PAD = 11;
const SEGMENT_HEIGHT = 26;

/** The reason a segment is unavailable is heard, never seen twice. */
const VISUALLY_HIDDEN_STYLE: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  overflow: "hidden",
  whiteSpace: NOWRAP,
  clipPath: "inset(50%)",
};

const LABEL_TRANSITION = crcTransition({
  properties: [CrcAnimatedProperty.Color],
  duration: CrcDuration.Hover,
  ease: CrcEase.Settle,
});

enum SegmentKey {
  ArrowLeft = "ArrowLeft",
  ArrowRight = "ArrowRight",
  Home = "Home",
  End = "End",
}

interface PillGeometry {
  readonly translateX: number;
  readonly translateY: number;
  readonly width: number;
  readonly height: number;
}

interface NextEnabledIndexInput {
  readonly options: readonly CresultcardSegmentOption[];
  readonly from: number;
  readonly delta: number;
}

/**
 * The next option a reader can actually choose, wrapping. Disabled options are
 * stepped over rather than landed on: arrowing onto a segment that refuses to
 * activate is the same lie as a disabled ghost.
 */
function nextEnabledIndex({
  options,
  from,
  delta,
}: NextEnabledIndexInput): number {
  const count = options.length;
  if (count === 0) {
    return -1;
  }
  let index = from;
  for (let step = 0; step < count; step += 1) {
    index = (index + delta + count) % count;
    const candidate = options[index];
    if (candidate != null && candidate.enabled) {
      return index;
    }
  }
  return -1;
}

export interface CresultcardSegmentedProps {
  readonly options: readonly CresultcardSegmentOption[];
  readonly value: string;
  readonly onChange: (id: string) => void;
  /** `Automatic` for cheap switches; `Manual` for anything that refetches. */
  readonly activation: CresultcardSegmentActivation;
  /** The id of the panel this strip governs. */
  readonly ariaControls: string;
  readonly label: string;
}

export function CresultcardSegmented({
  options,
  value,
  onChange,
  activation,
  ariaControls,
  label,
}: CresultcardSegmentedProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const baseId = useId();
  const stripRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Map<string, HTMLDivElement>>(
    new Map<string, HTMLDivElement>(),
  );
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [pill, setPill] = useState<PillGeometry | null>(null);

  /**
   * The tab stop follows the reader once they have moved it, and otherwise
   * sits on the selection — which is the model `role="tablist"` describes.
   */
  const rovingId =
    focusedId != null && options.some((option) => option.id === focusedId)
      ? focusedId
      : value;

  const registerOption = useCallback(
    (id: string, element: HTMLDivElement | null): void => {
      const refs = optionRefs.current;
      if (element == null) {
        refs.delete(id);
        return;
      }
      refs.set(id, element);
    },
    [],
  );

  /**
   * Measured from rects rather than from `offsetLeft`, so the same expression
   * lands the pill correctly in a right-to-left strip.
   *
   * Every input to that measurement is watched, because a pill whose transform
   * outlives its measurement sits under an option that is not the selection.
   * The option list and the value are dependencies; the strip AND every segment
   * are observed. Observing the strip alone is not enough: the strip's own box
   * is usually fixed by its parent, so a web font settling or a label changing
   * width reflows the segments INSIDE a box that never resizes — and a segment
   * that moves because an earlier sibling grew never resizes itself either,
   * which is why the observation covers all of them rather than the active one.
   * And where no option answers to `value` the pill is cleared rather than left
   * at its last geometry.
   */
  useLayoutEffect((): (() => void) | undefined => {
    const strip = stripRef.current;
    const active = optionRefs.current.get(value);
    if (strip == null || active == null) {
      setPill(null);
      return undefined;
    }
    const measure = (): void => {
      const stripRect = strip.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      const rtl = window.getComputedStyle(strip).direction === DIRECTION_RTL;
      setPill({
        translateX: rtl
          ? -(stripRect.right - activeRect.right)
          : activeRect.left - stripRect.left,
        translateY: activeRect.top - stripRect.top,
        width: activeRect.width,
        height: activeRect.height,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(strip);
    optionRefs.current.forEach((element): void => {
      observer.observe(element);
    });
    return (): void => {
      observer.disconnect();
    };
  }, [options, value]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      const strip = event.currentTarget;
      const rtl = window.getComputedStyle(strip).direction === DIRECTION_RTL;
      const forwardKey = rtl ? SegmentKey.ArrowLeft : SegmentKey.ArrowRight;
      const backwardKey = rtl ? SegmentKey.ArrowRight : SegmentKey.ArrowLeft;
      const currentIndex = options.findIndex(
        (option) => option.id === rovingId,
      );
      const key = event.key;
      let target = -1;
      if (key === forwardKey) {
        target = nextEnabledIndex({ options, from: currentIndex, delta: 1 });
      } else if (key === backwardKey) {
        target = nextEnabledIndex({ options, from: currentIndex, delta: -1 });
      } else if (key === SegmentKey.Home) {
        target = nextEnabledIndex({ options, from: -1, delta: 1 });
      } else if (key === SegmentKey.End) {
        target = nextEnabledIndex({
          options,
          from: options.length,
          delta: -1,
        });
      } else {
        return;
      }
      event.preventDefault();
      const next = target < 0 ? null : options[target];
      if (next == null) {
        return;
      }
      setFocusedId(next.id);
      optionRefs.current.get(next.id)?.focus();
      // Manual activation stops here: Cbutton's own Enter/Space handling is
      // what commits, so the arrow key has moved focus and nothing else.
      if (activation === CresultcardSegmentActivation.Automatic) {
        onChange(next.id);
      }
    },
    [activation, onChange, options, rovingId],
  );

  const handleSelect = useCallback(
    (option: CresultcardSegmentOption): void => {
      if (!option.enabled) {
        return;
      }
      setFocusedId(option.id);
      onChange(option.id);
    },
    [onChange],
  );

  const disabledOptions = options.filter((option) => !option.enabled);

  return (
    <div
      data-el="Cresultcard/Segmented/Root"
      style={{ position: "relative", minWidth: 0 }}
    >
      <div
        aria-label={label}
        onKeyDown={handleKeyDown}
        ref={stripRef}
        role="tablist"
        style={{
          position: "relative",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: SEGMENT_GAP,
          minWidth: 0,
          padding: STRIP_PAD,
          borderRadius: CRC_R_PILL,
          backgroundColor: CRC_WELL_BACKGROUND_COLOR,
          boxShadow: CRC_WELL_SHADOW,
        }}
      >
        {pill != null && (
          <span
            aria-hidden
            className={SEGMENT_PILL_CLASS_NAME}
            style={{
              position: "absolute",
              insetBlockStart: 0,
              insetInlineStart: 0,
              width: pill.width,
              height: pill.height,
              borderRadius: CRC_R_PILL,
              backgroundColor: CRC_TILE_BACKGROUND_COLOR,
              boxShadow: CRC_TILE_SHADOW,
              transform: `translate3d(${String(pill.translateX)}px, ${String(pill.translateY)}px, 0)`,
              pointerEvents: "none",
            }}
          />
        )}
        {options.map((option): ReactElement => {
          const selected = option.id === value;
          const describedBy =
            option.enabled || option.disabledReason == null
              ? undefined
              : `${baseId}${option.id}`;
          return (
            <Cbutton
              ariaDisabled={!option.enabled}
              ariaLabel={option.label}
              disableClickMotion
              disableHoverEffects
              key={option.id}
              onClick={(): void => {
                handleSelect(option);
              }}
              padding={0}
              preset={ButtonPreset.Ghost}
              radius={CRC_R_PILL}
              rootAttributes={{
                "aria-controls": ariaControls,
                "aria-describedby": describedBy,
                "aria-selected": selected,
                "data-el": "Cresultcard/Segmented/Entry",
                "data-crc-control": true,
              }}
              rootClassName={QUIET_HOVER_CLASS_NAME}
              rootRef={(element): void => {
                registerOption(option.id, element);
              }}
              rootRole={SEGMENT_TAB_ROLE}
              rootTabIndex={option.id === rovingId ? 0 : -1}
              title={option.enabled ? undefined : option.disabledReason}
              style={{
                ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
                position: "relative",
                zIndex: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
                minHeight: isMobile ? CRC_TOUCH_TARGET : SEGMENT_HEIGHT,
                paddingInline: SEGMENT_INLINE_PAD,
                borderRadius: CRC_R_PILL,
                whiteSpace: NOWRAP,
                cursor: option.enabled ? CURSOR_POINTER : CURSOR_DEFAULT,
                color: selected
                  ? CRC_ACCENT
                  : option.enabled
                    ? CRC_TEXT_MUTED
                    : CRC_TEXT_FAINT,
                // No `backgroundColor` here on purpose: an inline value would
                // outrank `.cresultcard__quiet-hover:hover` and the strip would
                // lose its only hover feedback.
                transition: LABEL_TRANSITION,
              }}
            >
              {option.label}
            </Cbutton>
          );
        })}
      </div>
      {disabledOptions.length > 0 && (
        <div
          style={VISUALLY_HIDDEN_STYLE}
        >
          {disabledOptions.map(
            (option): ReactElement => (
              <span
                id={`${baseId}${option.id}`}
                key={option.id}
              >
                {option.disabledReason}
              </span>
            ),
          )}
        </div>
      )}
    </div>
  );
}
